import type { NavigateFunction } from 'react-router';
import type { BookSummary } from '../../data/models';
import { createStore, useStore } from '../../lib/store';
import { overlayPath, tabPath, type OverlayId, type TabId } from './destinations';
import { HOME_LOCATION, type NavLocation } from './navigator';
import {
  backgroundOf,
  classifyLocation,
  hrefOf,
  isStackRoute,
  normalizePathname,
  type GateId,
  type LocationLike,
  type ModalId,
  type PushedPageId,
  type RouteClass,
} from './routes';

// The navigation API features call (contract §4). It works outside React: the
// shell binds react-router's navigate function and the committed location.
// Calls made before the router is mounted are queued.

export interface LayerInfo {
  /** What the top of the screen is. */
  kind: 'tab' | 'overlay' | 'pushed' | 'modal' | 'gate' | 'dev';
  /** The shell's tab (Menu while an overlay is open). */
  tab: TabId;
  overlay: OverlayId | null;
  page: PushedPageId | null;
  modal: ModalId | null;
  gate: GateId | null;
  /** Pushed pages and modal layers above the shell. */
  depth: number;
  pathname: string;
}

interface ShellNavState {
  activeTab: TabId;
  layer: LayerInfo;
}

const INITIAL_LAYER: LayerInfo = { kind: 'tab', tab: 'home', overlay: null, page: null, modal: null, gate: null, depth: 0, pathname: '/' };

/** Read-only for features; the shell writes it. */
export const shellNavStore = createStore<ShellNavState>({ activeTab: 'home', layer: INITIAL_LAYER });

/**
 * A nav-bar tap while an overlay is open (Flutter clears the overlay and
 * selects the tab in one setState). On the web it takes two history steps:
 * Back closes the overlay entry, then the tab replaces the entry it landed on.
 * The shell keeps drawing the overlay until the switch settles, so the entry
 * in between is never painted.
 */
interface TabSwitch {
  id: number;
  target: TabId;
  /** `closing`: waiting for the Back to land; `replacing`: waiting for the tab to replace that entry. */
  phase: 'closing' | 'replacing';
  /** The entry the pending step leaves (re-renders of it are not the next step). */
  fromKey: string;
}

const tabSwitchStore = createStore<TabSwitch | null>(null);
/** A switch that never settles (navigation lost) must not freeze the shell. */
const TAB_SWITCH_TIMEOUT_MS = 1000;
let tabSwitchSeq = 0;

let navigateFn: NavigateFunction | null = null;
let currentLocation: NavLocation = HOME_LOCATION;
let currentRoute: RouteClass = { kind: 'tab', tab: 'home' };
let currentBase: NavLocation = HOME_LOCATION;
const queue: Array<(navigate: NavigateFunction) => void> = [];
const tabMemory = new Map<TabId, NavLocation>();

/** Background chains in history state are capped so state stays small. */
const MAX_BACKGROUND_DEPTH = 3;

function run(action: (navigate: NavigateFunction) => void): void {
  if (navigateFn) action(navigateFn);
  else queue.push(action);
}

function historyIdx(): number {
  const state: unknown = typeof window !== 'undefined' ? window.history.state : null;
  const idx = state && typeof state === 'object' ? (state as Record<string, unknown>).idx : undefined;
  return typeof idx === 'number' ? idx : 0;
}

function sameLayer(a: LayerInfo, b: LayerInfo): boolean {
  return (
    a.kind === b.kind &&
    a.tab === b.tab &&
    a.overlay === b.overlay &&
    a.page === b.page &&
    a.modal === b.modal &&
    a.gate === b.gate &&
    a.depth === b.depth &&
    a.pathname === b.pathname
  );
}

export interface NavigationBinding {
  navigate: NavigateFunction;
  location: NavLocation;
  route: RouteClass;
  base: NavLocation;
  activeTab: TabId;
  layer: LayerInfo;
}

/** Shell only: called after every committed location. */
export function bindNavigation(binding: NavigationBinding): void {
  navigateFn = binding.navigate;
  currentLocation = binding.location;
  currentRoute = binding.route;
  currentBase = binding.base;
  const prev = shellNavStore.get();
  if (prev.activeTab !== binding.activeTab || !sameLayer(prev.layer, binding.layer)) {
    shellNavStore.set({ activeTab: binding.activeTab, layer: sameLayer(prev.layer, binding.layer) ? prev.layer : binding.layer });
  }
  queue.splice(0).forEach((action) => action(binding.navigate));
  settleTabSwitch(binding);
}

/**
 * Advances a pending overlay → tab switch on the first commit after each of
 * its steps, whatever that commit is (another overlay, a page, a gate…), so a
 * switch can never act on a later, unrelated navigation.
 */
function settleTabSwitch({ navigate, location, route }: NavigationBinding): void {
  const pending = tabSwitchStore.get();
  if (!pending || location.key === pending.fromKey) return;
  if (pending.phase === 'replacing' || (route.kind === 'tab' && route.tab === pending.target)) {
    tabSwitchStore.set(null);
    return;
  }
  const target = tabTarget(pending.target);
  tabSwitchStore.set({ ...pending, phase: 'replacing', fromKey: location.key });
  navigate(target.to, { replace: true, state: target.state });
}

function beginTabSwitch(target: TabId): void {
  const id = ++tabSwitchSeq;
  tabSwitchStore.set({ id, target, phase: 'closing', fromKey: currentLocation.key });
  setTimeout(() => {
    if (tabSwitchStore.get()?.id === id) tabSwitchStore.set(null);
  }, TAB_SWITCH_TIMEOUT_MS);
}

/** Shell only: an overlay → tab switch is between its history steps (keep drawing the overlay). */
export function useTabSwitchPending(): boolean {
  return useStore(tabSwitchStore, (s) => s !== null);
}

/** Shell only: the router unmounted (tests, hot reload). */
export function unbindNavigation(navigate: NavigateFunction): void {
  if (navigateFn === navigate) navigateFn = null;
}

/** Shell only: the last location of each tab, restored when the tab is selected again. */
export function rememberTabLocation(tab: TabId, location: NavLocation): void {
  tabMemory.set(tab, location);
}

/** Shell only: forget tab locations (the signed-in shell remounted for another account or library). */
export function resetTabMemory(): void {
  tabMemory.clear();
}

/** A copy of `loc` safe to store as `state.background` (nested backgrounds capped). */
export function backgroundSnapshot(loc: LocationLike, depth = MAX_BACKGROUND_DEPTH): LocationLike {
  const state = loc.state && typeof loc.state === 'object' ? { ...(loc.state as Record<string, unknown>) } : loc.state ?? null;
  if (state && typeof state === 'object') {
    const record = state as Record<string, unknown>;
    const inner = backgroundOf(loc);
    if (inner && depth > 1) record.background = backgroundSnapshot(inner, depth - 1);
    else delete record.background;
  }
  return { pathname: loc.pathname, search: loc.search ?? '', hash: loc.hash ?? '', key: loc.key, state };
}

function pathnameOf(path: string): string {
  return normalizePathname(new URL(path, 'http://plume.invalid').pathname);
}

/** Paths whose layer depends on `state.pushed` (`/settings` is also a tab, `/welcome` also a gate). */
function needsPushedFlag(path: string): boolean {
  const pathname = pathnameOf(path);
  return pathname === '/settings' || pathname === '/welcome' || pathname.startsWith('/welcome/');
}

function pushedState(path: string, state: unknown): unknown {
  if (!needsPushedFlag(path)) return state;
  const base = state && typeof state === 'object' ? (state as Record<string, unknown>) : state === undefined ? {} : { data: state };
  return { ...base, pushed: true };
}

/** Pushed pages and modal layers (they sit above the shell and remember what is underneath). */
function isStackPath(path: string, state: unknown): boolean {
  const url = new URL(path, 'http://plume.invalid');
  return isStackRoute(classifyLocation({ pathname: url.pathname, search: url.search, state }, { signedIn: true }));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/** Adds `background` to a location state (an explicit one wins; non-object states are left alone). */
function withBackground(state: unknown, background: LocationLike | null): unknown {
  if (!background) return state;
  if (state === undefined || state === null) return { background };
  if (!isPlainObject(state) || 'background' in state) return state;
  return { ...state, background };
}

function tabTarget(id: TabId): { to: string; state: unknown } {
  const remembered = tabMemory.get(id);
  return remembered ? { to: hrefOf(remembered), state: remembered.state } : { to: tabPath(id), state: null };
}

export const nav = {
  /**
   * Selects a bottom-nav tab. Re-selecting the current tab is a no-op here
   * (SHELL-014; the nav bar itself scrolls the shown tab back to the top);
   * switching tabs replaces the history entry (tabs are not a back stack in
   * Flutter); from an overlay it closes the overlay first.
   */
  tab(id: TabId): void {
    run((navigate) => {
      const pending = tabSwitchStore.get();
      if (pending?.phase === 'closing') {
        // The overlay is still closing: the switch lands on the latest tab tapped.
        if (pending.target !== id) tabSwitchStore.set({ ...pending, target: id });
        return;
      }
      const route = currentRoute;
      const target = tabTarget(id);
      if (route.kind === 'tab') {
        if (route.tab !== id) navigate(target.to, { replace: true, state: target.state });
        return;
      }
      if (route.kind === 'overlay') {
        if (historyIdx() > 0) {
          beginTabSwitch(id);
          navigate(-1);
        } else {
          navigate(target.to, { replace: true, state: target.state });
        }
        return;
      }
      navigate(target.to, { state: target.state });
    });
  },

  /** Opens a Menu destination that replaces the tab content (Back closes it). */
  overlay(id: OverlayId): void {
    run((navigate) => {
      const route = currentRoute;
      if (route.kind === 'overlay') {
        if (route.overlay !== id) navigate(overlayPath(id), { replace: true });
        return;
      }
      navigate(overlayPath(id));
    });
  },

  /**
   * Pushes a full page above the shell. `state` reaches the page through
   * `useLocation().state`; the shell adds `background` (the current location)
   * so a reload rebuilds the same layers underneath.
   */
  push(path: string, state?: unknown): void {
    run((navigate) => {
      const next = pushedState(path, state);
      const background = isStackPath(path, next) ? backgroundSnapshot(currentLocation) : null;
      navigate(path, { state: withBackground(next, background) });
    });
  },

  /**
   * Replaces the current history entry (e.g. a section switch inside a
   * screen). A page or modal replacing another keeps what was underneath it.
   */
  replace(path: string, state?: unknown): void {
    run((navigate) => {
      const next = pushedState(path, state);
      const background = isStackPath(path, next) ? backgroundOf(currentLocation) : null;
      navigate(path, { replace: true, state: withBackground(next, background) });
    });
  },

  /**
   * Goes back one entry. Open sheets and dialogs close first (they own a
   * history entry). Without in-app history (deep link) it goes to the
   * logical parent instead of leaving the app.
   */
  back(): void {
    run((navigate) => {
      if (historyIdx() > 0) {
        navigate(-1);
        return;
      }
      const route = currentRoute;
      if (route.kind === 'modal') nav.closeModal();
      else if (route.kind === 'pushed') navigate(hrefOf(currentBase), { replace: true, state: currentBase.state });
      else if (route.kind === 'overlay') navigate(tabPath('menu'), { replace: true });
    });
  },

  /** Opens Book Details as a modal layer over the current screen. */
  openBook(bookId: string, opts: { seed?: BookSummary } = {}): void {
    run((navigate) => {
      const path = `/book/${encodeURIComponent(bookId)}`;
      if (currentRoute.kind === 'modal' && currentRoute.modal === 'book' && normalizePathname(currentLocation.pathname) === path) return;
      navigate(path, { state: { background: backgroundSnapshot(currentLocation), seed: opts.seed } });
    });
  },

  /** Opens the full player as a modal layer (covers the nav bar and mini player). */
  openPlayer(): void {
    run((navigate) => {
      if (currentRoute.kind === 'modal' && currentRoute.modal === 'player') return;
      navigate('/player', { state: { background: backgroundSnapshot(currentLocation) } });
    });
  },

  /**
   * Closes the current modal layer (Book Details or the player): history
   * back when it was opened in-app, otherwise it is replaced by what lies
   * underneath (its background, else the shell's tab or overlay).
   */
  closeModal(): void {
    run((navigate) => {
      if (currentRoute.kind !== 'modal') return;
      const background = backgroundOf(currentLocation);
      if (background && historyIdx() > 0) {
        navigate(-1);
        return;
      }
      const target = background ?? currentBase;
      navigate(hrefOf(target), { replace: true, state: target.state ?? null });
    });
  },
};

/** A tab URL from outside (notification, shortcut): its search/hash become the tab's remembered location. */
function openTab(tab: TabId, loc: { pathname: string; search: string; hash: string }): void {
  const href = hrefOf(loc);
  const specific = loc.search !== '' || loc.hash !== '';
  if (specific) rememberTabLocation(tab, { ...loc, state: null, key: `url:${href}` });
  run((navigate) => {
    const onTab = currentRoute.kind === 'tab' && currentRoute.tab === tab;
    if (specific && onTab && hrefOf(currentLocation) !== href) navigate(href, { replace: true });
    else nav.tab(tab);
  });
}

/**
 * Routes an in-app URL (service-worker notification clicks, shortcuts) the
 * way the UI would: tabs switch (replacing history), overlays open over the
 * Menu tab, book and player URLs open as modal layers, other pages push.
 */
export function openAppUrl(url: string): void {
  const parsed = new URL(url, 'http://plume.invalid');
  const pathname = normalizePathname(parsed.pathname);
  const route = classifyLocation({ pathname, search: parsed.search }, { signedIn: true });
  switch (route.kind) {
    case 'modal': {
      if (route.modal === 'player') {
        nav.openPlayer();
        return;
      }
      const id = /^\/(?:book|item)\/([^/]+)$/.exec(pathname)?.[1];
      if (id) nav.openBook(decodeURIComponent(id));
      return;
    }
    case 'tab':
      openTab(route.tab, { pathname, search: parsed.search, hash: parsed.hash });
      return;
    case 'overlay':
      nav.overlay(route.overlay);
      return;
    case 'redirect':
      openAppUrl(route.to);
      return;
    default:
      nav.push(`${pathname}${parsed.search}${parsed.hash}`);
  }
}

/** The shell's selected tab (Menu while an overlay is open). */
export function useActiveTab(): TabId {
  return useStore(shellNavStore, (s) => s.activeTab);
}

/** What is on top: tab, overlay, pushed page, modal layer or gate. */
export function useLayer(): LayerInfo {
  return useStore(shellNavStore, (s) => s.layer);
}
